import React from "react";
import { Link } from "react-router-dom";
import {
    Home as HomeIcon, ShieldCheck, BellRing, FileText, Key, CloudLightning, Hammer,
    MapPin, Award, Clock3, Languages, HandCoins, ArrowRight,
    Paintbrush, HardHat, ClipboardCheck,
} from "lucide-react";

import Reveal from "@/components/Reveal";
import ConsultationForm from "@/components/ConsultationForm";
import HomeTestimonials from "@/components/HomeTestimonials";
import { SERVICES, IMPROVEMENTS, LIFESTYLE, TRUST_INDICATORS, TRUST_BAR, WHY_PKM_KEYS } from "@/lib/content";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO from "@/lib/useSEO";

const ICON_MAP = {
    Home: HomeIcon, ShieldCheck, BellRing, FileText, Key, CloudLightning, Hammer,
    MapPin, Award, Clock3, Languages, HandCoins,
    Paintbrush, HardHat, ClipboardCheck,
};

// HERO_IMAGE — modern Mexican villa with infinity pool (Cabo luxury aesthetic). Easily swappable.
const HERO_IMAGE = "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?auto=format&fit=crop&w=2400&q=85";
// ABOUT_IMAGE — luxury Cabo living room with view
const ABOUT_IMAGE = "https://images.unsplash.com/photo-1600585152915-d208bec867a1?auto=format&fit=crop&w=1800&q=85";
// ABOUT_IMAGE_SMALL — modern Cabo villa exterior
const ABOUT_IMAGE_SMALL = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=85";

export default function Home() {
    const { t, lang } = useLanguage();

    useSEO({
        title: lang === "es"
            ? "Administración de Propiedades de Lujo en Los Cabos | Pacific Key Management"
            : "Pacific Key Management | Luxury Property Management Los Cabos",
        description: lang === "es"
            ? "Administración boutique de propiedades y estates de lujo en Los Cabos — supervisión proactiva, servicios de concierge, coordinación de mantenimiento y atención personalizada al propietario."
            : "Boutique luxury property and estate management in Los Cabos, providing proactive home oversight, concierge services, maintenance coordination and personalized owner support.",
        path: "/",
        keywords: "Luxury Property Management Los Cabos, Villas del Mar Property Management, Palmilla Estate Management, Luxury Concierge Los Cabos, Vacation Home Management Los Cabos, High-End Property Management Cabo",
        breadcrumbs: [{ name: "Home", path: "/" }],
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Luxury Property & Estate Management",
            provider: { "@type": "Organization", name: "Pacific Key Management", url: "https://pacifickeymanagement.com" },
            areaServed: { "@type": "Place", name: "Los Cabos, Baja California Sur, Mexico" },
            url: "https://pacifickeymanagement.com/",
        },
    });

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative h-screen min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover animate-ken-burns" />
                    <div className="absolute inset-0 bg-navy/55" />
                    <div className="absolute inset-0 bg-gradient-to-b from-navy/30 via-transparent to-navy/70" />
                </div>
                <div className="relative h-full container-luxe flex flex-col justify-center pt-20">
                    <p className="overline text-gold animate-fade-up" style={{ animationDelay: "0.3s" }}>
                        {t("home.heroEyebrow")}
                    </p>
                    <h1 className="mt-6 font-serif text-pearl text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] max-w-5xl tracking-tight animate-fade-up" style={{ animationDelay: "0.55s" }}>
                        {t("home.heroTitle1")}<br />
                        <span className="italic font-light">{t("home.heroTitle2")}</span>
                    </h1>
                    <p className="mt-8 text-pearl/85 text-base sm:text-lg max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.8s" }}>
                        {t("home.heroSubtitle")}
                    </p>
                    <div className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "1.05s" }}>
                        <Link to="/contact" className="btn-luxe-light" data-testid="hero-cta-consultation">
                            {t("home.ctaConsult")}
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                        <Link to="/services" className="btn-luxe-gold" data-testid="hero-cta-services">
                            {t("home.ctaServices")}
                        </Link>
                    </div>
                </div>
                <div className="absolute bottom-10 inset-x-0 flex justify-center animate-fade-in" style={{ animationDelay: "1.4s" }}>
                    <div className="flex flex-col items-center gap-2 text-pearl/70">
                        <span className="text-[0.65rem] tracking-[0.4em] uppercase">{t("common.scrollDown")}</span>
                        <span className="w-px h-10 bg-gold/60" />
                    </div>
                </div>
            </section>

            {/* TRUST BAR — subtle credential strip under hero */}
            <section className="bg-navy text-pearl border-y border-pearl/10" data-testid="trust-bar">
                <div className="container-luxe py-7">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-5 items-center">
                        {TRUST_BAR.map((k, i) => (
                            <div
                                key={k}
                                data-testid={`trust-bar-item-${i}`}
                                className="flex items-center justify-center sm:justify-start gap-3"
                            >
                                <span className="w-1 h-1 rounded-full bg-gold flex-shrink-0" />
                                <span className="text-[0.7rem] sm:text-[0.72rem] tracking-[0.15em] uppercase text-pearl/75 leading-snug">
                                    {t(`trust.${k}`)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRUST */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="trust-section">
                <div className="container-luxe">
                    <Reveal className="text-center max-w-3xl mx-auto mb-16">
                        <p className="overline text-gold">{t("home.trustEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-balance">{t("home.trustTitle")}</h2>
                    </Reveal>
                    <div className="grid grid-cols-2 lg:grid-cols-3 border-t border-l border-navy/10">
                        {TRUST_INDICATORS.map((tr, i) => {
                            const Icon = ICON_MAP[tr.icon] || HomeIcon;
                            return (
                                <Reveal key={tr.key} delay={i * 80} className="flex flex-col items-start justify-center gap-5 p-8 sm:p-12 border-r border-b border-navy/10 min-h-[180px] hover:bg-sand/40 transition-colors duration-500" data-testid={`trust-card-${i}`}>
                                    <Icon className="w-7 h-7 text-gold" strokeWidth={1.25} />
                                    <p className="font-serif text-2xl leading-tight max-w-[240px]">{t(`trust.${tr.key}`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="about-preview-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <Reveal className="lg:col-span-6 relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img src={ABOUT_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="hidden md:block absolute -bottom-12 -right-8 w-48 h-64 lg:w-64 lg:h-80 overflow-hidden border-8 border-pearl shadow-xl">
                            <img src={ABOUT_IMAGE_SMALL} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-6 space-y-7">
                        <p className="overline text-gold">{t("home.aboutEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-balance">{t("home.aboutTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("home.aboutBody1")}</p>
                        <p className="text-navy/75 leading-relaxed">{t("home.aboutBody2")}</p>
                        <Link to="/about" className="inline-flex items-center gap-3 text-[0.7rem] uppercase tracking-[0.28em] text-navy hover:text-gold transition-colors" data-testid="about-preview-cta">
                            {t("home.aboutLink")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* SERVICES */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="services-overview-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("home.servicesEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("home.servicesTitle")}</h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10">
                        {SERVICES.map((s, i) => {
                            const Icon = ICON_MAP[s.icon] || HomeIcon;
                            return (
                                <Reveal key={s.key} delay={i * 90} className="bg-pearl p-10 lg:p-12 min-h-[320px] flex flex-col group hover:bg-sand/30 transition-colors duration-500" data-testid={`service-card-${s.key}`}>
                                    <Icon className="w-8 h-8 text-gold" strokeWidth={1.25} />
                                    <h3 className="mt-8 font-serif text-2xl">{t(`services_data.${s.key}.title`)}</h3>
                                    <p className="mt-4 text-sm text-navy/70 leading-relaxed flex-1">{t(`services_data.${s.key}.summary`)}</p>
                                    <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-navy group-hover:text-gold transition-colors" data-testid={`service-cta-${s.key}`}>
                                        {t("common.discover")} <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PROPERTY IMPROVEMENTS & PROJECT OVERSIGHT */}
            <section className="bg-navy text-pearl py-24 sm:py-32" data-testid="improvements-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("home.improvementsEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("home.improvementsTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="mt-7 text-pearl/75 leading-relaxed max-w-2xl">{t("home.improvementsSubtitle")}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-pearl/10">
                        {IMPROVEMENTS.map((imp, i) => {
                            const Icon = ICON_MAP[imp.icon] || ShieldCheck;
                            return (
                                <Reveal
                                    key={imp.key}
                                    delay={i * 90}
                                    className="group relative bg-navy p-10 min-h-[420px] flex flex-col overflow-hidden"
                                    data-testid={`improvement-card-${imp.key}`}
                                >
                                    <div className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity duration-700">
                                        <img src={imp.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/85 to-navy/30" />
                                    </div>
                                    <div className="relative flex flex-col flex-1">
                                        <Icon className="w-8 h-8 text-gold" strokeWidth={1.25} />
                                        <h3 className="mt-8 font-serif text-pearl text-2xl leading-tight">{t(`improvements.${imp.key}.title`)}</h3>
                                        <span className="block w-8 h-px bg-gold my-5" />
                                        <p className="text-sm text-pearl/75 leading-relaxed flex-1">{t(`improvements.${imp.key}.body`)}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={400} className="mt-14 flex justify-start">
                        <Link
                            to="/maintenance-construction"
                            className="inline-flex items-center gap-3 text-[0.7rem] uppercase tracking-[0.28em] text-pearl hover:text-gold transition-colors"
                            data-testid="improvements-cta"
                        >
                            {t("common.discover")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* LIFESTYLE */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="lifestyle-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("home.lifestyleEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("home.lifestyleTitle")}</h2>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                        {LIFESTYLE.map((l, i) => (
                            <Reveal key={l.key} delay={i * 70} className="group relative aspect-[4/5] overflow-hidden" data-testid={`lifestyle-card-${l.key}`}>
                                <img src={l.image} alt={t(`lifestyle_data.${l.key}`)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" loading="lazy" />
                                <div className="absolute inset-0 bg-navy/30 group-hover:bg-navy/45 transition-colors duration-500" />
                                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                                    <h3 className="font-serif text-pearl text-xl sm:text-2xl">{t(`lifestyle_data.${l.key}`)}</h3>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY PKM */}
            <section className="bg-sand/50 py-24 sm:py-32" data-testid="why-pkm-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-20">
                        <p className="overline text-gold">{t("home.whyEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("home.whyTitle")}</h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
                        {WHY_PKM_KEYS.map((k, i) => (
                            <Reveal key={k} delay={i * 80} data-testid={`why-pkm-item-${i}`}>
                                <span className="block text-gold font-serif text-3xl mb-3">0{i + 1}</span>
                                <h3 className="font-serif text-2xl mb-3">{t(`whyPkm.${k}.title`)}</h3>
                                <span className="block w-8 h-px bg-gold mb-4" />
                                <p className="text-navy/70 text-sm leading-relaxed">{t(`whyPkm.${k}.body`)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS — real published testimonials only (hidden until 3+ published) */}
            <HomeTestimonials />

            {/* CTA */}
            <section className="bg-sand/60 py-24 sm:py-32" data-testid="final-cta-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20">
                    <Reveal className="lg:col-span-5 space-y-6">
                        <p className="overline text-gold">{t("home.ctaEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-balance">{t("home.ctaTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed max-w-md">{t("home.ctaBody")}</p>
                        <div className="pt-4 space-y-2 text-sm text-navy/70">
                            <p><span className="overline text-navy/55 mr-2">{t("home.ctaDirect")}</span> +1 (310) 770-6585</p>
                            <p><span className="overline text-navy/55 mr-2">{t("home.ctaEmail")}</span> info@pacifickeymanagement.com</p>
                        </div>
                    </Reveal>
                    <Reveal delay={140} className="lg:col-span-7 bg-pearl p-8 sm:p-12 border border-navy/10">
                        <ConsultationForm />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
